/**
 * RedemptionPanels — shared UI for the mode-based access-code flows
 * (2026-08). Used by /pricing and /start so the two entry points stay
 * pixel-consistent and deterministic:
 *
 *   AccountQuestionPanel   — signed-out, valid unused code: ask (nicely)
 *       whether the user already has an account, and route sign-in vs
 *       create-account accordingly. Signing in first is required so the
 *       code applies to the right account (active → wallet credit,
 *       trial → extension, fresh → trial start).
 *
 *   RedemptionOutcomePanel — signed-in redemption confirmations for the
 *       non-onboarding modes: 'wallet_credit' (active account, credit
 *       added, no expiry mentioned) and 'trial_extension' (credit added +
 *       clock extended).
 *
 * Copy is lowercase by convention (matches wallet/portal style).
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import Spinner from '../common/Spinner';
import { usdToWu, formatWu, WU_LABEL } from '../../config/workUnits';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1.25rem;
  padding: 1.75rem 1.5rem;
  box-sizing: border-box;
  width: 100%;
  animation: ${fadeIn} 0.35s ease both;
  text-align: center;

  @media (min-width: 640px) {
    padding: 2rem 2.25rem;
  }

  @media (min-width: 1024px) {
    padding: 2.5rem 3rem;
  }
`;

const PanelIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.6rem;
`;

const PanelTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  /* Avoid a lone orphaned word at tablet shell widths (progressive
     enhancement — older Safari ignores it harmlessly). */
  text-wrap: balance;
`;

const SpinnerRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.9rem;
`;

const PanelBody = styled.p`
  font-size: 0.9rem;
  color: #555;
  line-height: 1.65;
  max-width: 60ch;
  margin: 0 auto 1.25rem;

  @media (min-width: 1024px) {
    font-size: 0.95rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 420px) {
    flex-direction: column;
  }
`;

const PrimaryAction = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
`;

const SecondaryAction = styled.button`
  background: #fff;
  color: var(--brand-text);
  border: 1.5px solid #D8D5CE;
  border-radius: 0.85rem;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: #F7F6F3; border-color: #B9B4A9; }
`;

export function formatCreditDollars(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString()}`
    : `$${dollars.toFixed(2)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Account question — signed-out, valid unused code
// ─────────────────────────────────────────────────────────────────────────────

export interface AccountQuestionPanelProps {
  onSignIn: () => void;
  onCreateAccount: () => void;
}

export const AccountQuestionPanel: React.FC<AccountQuestionPanelProps> = ({ onSignIn, onCreateAccount }) => (
  <Panel data-testid="redeem-account-question">
    <PanelIcon>👋</PanelIcon>
    <PanelTitle>quick question — do you already have a prest0 account?</PanelTitle>
    <PanelBody>
      if you do, sign in first so your code applies to the right account. if you're
      new here, we'll get you set up in under a minute.
    </PanelBody>
    <ButtonRow>
      <PrimaryAction type="button" onClick={onSignIn}>
        yes — sign me in
      </PrimaryAction>
      <SecondaryAction type="button" onClick={onCreateAccount}>
        no — create my account
      </SecondaryAction>
    </ButtonRow>
  </Panel>
);

// ─────────────────────────────────────────────────────────────────────────────
// Redemption outcome — wallet credit / trial extension confirmations
// ─────────────────────────────────────────────────────────────────────────────

export interface RedemptionOutcome {
  mode: 'wallet_credit' | 'trial_extension';
  /** wallet_credit: full amount; trial_extension: amount added. */
  creditCents: number;
  /** trial_extension only — ISO string of the new end date. */
  expiresAt?: string | null;
}

export interface RedemptionOutcomePanelProps {
  outcome: RedemptionOutcome;
  onGoToWorkspace: () => void;
  onGoToWallet: () => void;
}

export const RedemptionOutcomePanel: React.FC<RedemptionOutcomePanelProps> = ({
  outcome,
  onGoToWorkspace,
  onGoToWallet,
}) => {
  const credit = formatCreditDollars(outcome.creditCents);

  if (outcome.mode === 'wallet_credit') {
    // Display-only WU conversion at the Standard Rate ($2.18 / WU); the
    // credited amount itself (cents) is unchanged.
    const creditWu = formatWu(usdToWu(outcome.creditCents / 100));
    return (
      <Panel data-testid="redeem-outcome-wallet-credit">
        <PanelIcon>🎉</PanelIcon>
        <PanelTitle>credit applied</PanelTitle>
        <PanelBody>
          {credit} in platform credit has been added to your account as Work
          Units (≈ {creditWu} {WU_LABEL}) — it's yours to use whenever you like.
        </PanelBody>
        <ButtonRow>
          <PrimaryAction type="button" onClick={onGoToWallet}>
            view your wallet
          </PrimaryAction>
          <SecondaryAction type="button" onClick={onGoToWorkspace}>
            back to your workspace
          </SecondaryAction>
        </ButtonRow>
      </Panel>
    );
  }

  const endsOn = outcome.expiresAt
    ? new Date(outcome.expiresAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  return (
    <Panel data-testid="redeem-outcome-trial-extension">
      <PanelIcon>🎉</PanelIcon>
      <PanelTitle>your trial just grew</PanelTitle>
      <PanelBody>
        we've added {credit} in credit to your trial
        {endsOn ? ` and extended it through ${endsOn}` : ' and extended your time'} —
        pick up right where you left off.
      </PanelBody>
      <ButtonRow>
        <PrimaryAction type="button" onClick={onGoToWorkspace}>
          back to your workspace
        </PrimaryAction>
      </ButtonRow>
    </Panel>
  );
};

export interface RedeemProgressPanelProps {
  /**
   * Where the redemption is in its journey:
   *   'checking'  — code is being validated (pre-redeem)
   *   'applying'  — redeemAccessCode in flight (auth, RBAC bootstrap,
   *                 welcome seeding — can take several seconds)
   *   'applied'   — success beat before navigation to onboarding
   */
  stage: 'checking' | 'applying' | 'applied';
}

/**
 * RedeemProgressPanel — the single in-shell progress card for code
 * redemption (2026-08-09 owner directive: after every submission it must be
 * abundantly clear the process is moving along). Extracted from the two
 * byte-similar inline panels in StartTrial.tsx / Pricing.tsx so the /start
 * and /pricing funnels cannot drift. Shows a live spinner while work is in
 * flight and a calm success beat once applied. Never renders a URL or code.
 */
export const RedeemProgressPanel: React.FC<RedeemProgressPanelProps> = ({ stage }) => (
  <Panel data-testid={`redeem-progress-${stage}`}>
    {stage === 'applied'
      ? <PanelIcon>✨</PanelIcon>
      : <SpinnerRow><Spinner /></SpinnerRow>}
    <PanelTitle>
      {stage === 'checking' && 'checking your invite code…'}
      {stage === 'applying' && 'applying your invite code…'}
      {stage === 'applied' && 'code applied'}
    </PanelTitle>
    <PanelBody style={{ marginBottom: 0 }}>
      {stage === 'checking' && 'one moment — verifying your invitation.'}
      {stage === 'applying' && 'hold tight — securely linking your code to your account.'}
      {stage === 'applied' && 'taking you to your workspace setup…'}
    </PanelBody>
  </Panel>
);
